import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export type SubscriptionStatus = 
  | 'trialing' 
  | 'active' 
  | 'past_due' 
  | 'canceled' 
  | 'unpaid' 
  | 'incomplete' 
  | 'none';

export interface SubscriptionAccess {
  hasAccess: boolean;
  status: SubscriptionStatus;
  isInGracePeriod: boolean;
  gracePeriodEnd: Date | null;
  trialEnd: Date | null;
  daysUntilGracePeriodEnd: number | null;
  daysUntilTrialEnd: number | null;
  isLoading: boolean;
  isError: boolean;
}

interface SubscriptionAccessRow {
  has_access: boolean;
  status: SubscriptionStatus | null;
  is_in_grace_period: boolean;
  grace_period_end: string | null;
  trial_end: string | null;
  days_until_grace_period_end: number | null;
  days_until_trial_end: number | null;
}

export function useSubscriptionAccess(): SubscriptionAccess {
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['subscription-access', user?.org_id],
    queryFn: async (): Promise<SubscriptionAccessRow | null> => {
      if (!user?.org_id) return null;

      // First get the organization data for local trial period calculation
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', user.org_id)
        .single();

      if (!orgData) return null;

      // Calculate 7-day trial period locally
      const createdAt = new Date(orgData.created_at);
      const trialPeriodEnd = new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000);
      const now = new Date();
      const isInTrialPeriod = now < trialPeriodEnd;

      if (isInTrialPeriod) {
        return {
          has_access: true,
          status: 'trialing',
          is_in_grace_period: false,
          grace_period_end: null,
          trial_end: trialPeriodEnd.toISOString(),
          days_until_grace_period_end: null,
          days_until_trial_end: Math.floor((trialPeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        };
      }

      // If outside trial period, use RPC
      const { data: accessData, error: accessError } = await supabase
        .rpc('get_subscription_access', { org_id: user.org_id })
        .single();

      if (accessError) {
        console.error('Error fetching subscription access:', accessError);
        return null;
      }

      return accessData as SubscriptionAccessRow;
    },
    enabled: !!user?.org_id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: true,
  });

  // Default state when no data or loading
  const defaultState: SubscriptionAccess = {
    hasAccess: false,
    status: 'none',
    isInGracePeriod: false,
    gracePeriodEnd: null,
    trialEnd: null,
    daysUntilGracePeriodEnd: null,
    daysUntilTrialEnd: null,
    isLoading,
    isError,
  };

  if (!data || isLoading) {
    return defaultState;
  }

  return {
    hasAccess: data.has_access ?? false,
    status: data.status ?? 'none',
    isInGracePeriod: data.is_in_grace_period ?? false,
    gracePeriodEnd: data.grace_period_end ? new Date(data.grace_period_end) : null,
    trialEnd: data.trial_end ? new Date(data.trial_end) : null,
    daysUntilGracePeriodEnd: data.days_until_grace_period_end,
    daysUntilTrialEnd: data.days_until_trial_end,
    isLoading: false,
    isError,
  };
}

/**
 * Check if user should see subscription banners (admins only)
 */
export function useShouldShowSubscriptionBanner(): boolean {
  const { user } = useAuth();
  const { status, isInGracePeriod, daysUntilTrialEnd } = useSubscriptionAccess();

  // Only org admins should see banners
  const isAdmin = user?.role === 'org_admin';

  if (!isAdmin) return false;

  // Show banner if:
  // - No subscription (status is 'none')
  // - In trial (ending soon)
  // - In grace period
  // - Payment failed
  // - Subscription canceled
  return (
    status === 'none' ||
    (status === 'trialing' && (daysUntilTrialEnd ?? 0) <= 7) ||
    status === 'past_due' ||
    isInGracePeriod ||
    status === 'canceled' ||
    status === 'unpaid'
  );
}

/**
 * Get the banner message based on subscription status
 */
export function useSubscriptionBannerMessage(): string | null {
  const { status, daysUntilGracePeriodEnd, daysUntilTrialEnd } = useSubscriptionAccess();

  switch (status) {
    case 'none': {
      // No subscription - show trial offer with counter
      const trialDays = daysUntilTrialEnd ?? 14; // Default to 14 days trial
      if (trialDays > 0) {
        return `Start your 14-day free trial! ${trialDays} days remaining. Add payment details to continue after trial.`;
      }
      return 'Your trial has ended. Subscribe to continue using CareQuote.';
    }
    case 'trialing': {
      const trialDays = daysUntilTrialEnd ?? 0;
      if (trialDays <= 3) {
        return `Your trial ends in ${trialDays} day${trialDays === 1 ? '' : 's'}. Add a payment method to continue.`;
      }
      if (trialDays <= 7) {
        return `Your trial ends in ${trialDays} days. Consider adding a payment method.`;
      }
      return null;
    }
    case 'past_due':
    case 'unpaid': {
      const graceDays = daysUntilGracePeriodEnd ?? 0;
      if (graceDays > 0) {
        return `Payment failed. Update your payment method within ${graceDays} day${graceDays === 1 ? '' : 's'} to maintain access.`;
      }
      return 'Your subscription has ended. Update payment to restore access.';
    }
    case 'canceled':
      return 'Your subscription has been canceled. Reactivate to restore access.';

    case 'incomplete':
      return 'Complete your subscription setup to gain access.';

    default:
      return null;
  }
}
